import {
	IChatEventPostbackMessage,
	IChatEventSurveyDataMessage,
	IMessageCommandMeta,
	isEventMessageSurveyData,
	isEventMessagePostback,
	isCommandMessageForm,
	isCommandMessageSurvey,
	isCommandMessage,
	isEventMessage,
	isPopupMessage,
	isCommandMessageFormFullscreen,
	SenderSource,
	isCommandMessageSurveyInline,
} from '@enuan/chat__utils';
import { IMessageContextState } from '../../components/message/message.context';
import { IClientMessage } from '../../interfaces/IClientMessage';
import { messagesAdapter } from '../../reduxStore/adapters/messages.adapter';
import { IAppState } from '../../reduxStore/store';
import { isRenderableMessage } from '../../utils/isRenderableMessage';
import { haveSameDay } from '../../utils/getTimeParts';
import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { first as _first, last as _last } from 'lodash';

export const messagesSelectors = messagesAdapter.getSelectors<{
	messages: ReturnType<typeof messagesAdapter.getInitialState>;
}>((state) => state.messages);

/**
 * Returns `true` when the server has confirmed there are no more older
 * messages to load. Reset to `false` after a trim so the removed messages
 * can be re-fetched on the next upward scroll.
 */
export const getHistoryExhausted = (state: IAppState): boolean =>
	state.messages.historyExhausted;

export const getRenderableMessages = createDraftSafeSelector(
	messagesSelectors.selectAll,
	(messages) => messages.filter(isRenderableMessage),
);

export const getUnreadMessages = createDraftSafeSelector(
	[getRenderableMessages],
	(allMessages): Array<IClientMessage> => {
		const unreadMessages = allMessages.filter((message) => !message._meta.read);
		return unreadMessages;
	},
);

export const getFirstMessage = (state: IAppState): IClientMessage | undefined =>
	_first(messagesSelectors.selectAll(state));

export const getLastRenderableMessage = createDraftSafeSelector(
	[getRenderableMessages],
	(renderableMessages): IClientMessage | undefined => _last(renderableMessages),
);

export const getLastMessage = (state: IAppState): IClientMessage | undefined =>
	_last(messagesSelectors.selectAll(state));

export const getLastUnreadPopupMessage = createDraftSafeSelector(
	getUnreadMessages,
	(messages) => messages.toReversed().find(isPopupMessage),
);

export const getHasLastUnreadPopupMessage = createDraftSafeSelector(
	getLastUnreadPopupMessage,
	(message) => !!message,
);

const areSameGroup = (a: IClientMessage, b: IClientMessage) => {
	const sameSender = a.sender === b.sender;
	return sameSender && haveSameDay(a.timestamp, b.timestamp);
};

const isAlone = (message: IClientMessage) => {
	const alone = isCommandMessage(message) || isEventMessage(message);

	return alone;
};

export const getMessagesBlocks = createDraftSafeSelector(
	getRenderableMessages,
	(messages) => {
		return messages.reduce<(typeof messages)[]>((prev, message) => {
			if (!prev.length) return [[message]];
			const { timestamp } = message;
			const alone = isAlone(message);

			const prevBlock = prev[prev.length - 1];
			const messageSamplePrevBlock = prevBlock[0];

			const prevIsAlone = isAlone(messageSamplePrevBlock);
			if (
				areSameGroup(messageSamplePrevBlock, message) &&
				!alone &&
				!prevIsAlone
			) {
				const meanTime =
					prevBlock.reduce<number>(
						(p, { timestamp }) => p + new Date(timestamp!).valueOf(),
						0,
					) / prevBlock.length;

				const inTimeRange =
					new Date(timestamp!).valueOf() <= meanTime + 10 * 60 * 1000;

				if (inTimeRange || (!messageSamplePrevBlock.timestamp && !timestamp)) {
					return prev.map((block, i) =>
						i === prev.length - 1 ? [...block, message] : block,
					);
				}
			}
			return [...prev, [message]];
		}, []);
	},
);

export const getMessagesBlocksProps = createDraftSafeSelector(
	getMessagesBlocks,
	(messagesBlocks) =>
		messagesBlocks
			.map((messagesBlock, index) => {
				const isFirstBlock = index === 0;
				const isLastBlock = messagesBlocks.length - 1 === index;
				const isDifferentDayBlock = !haveSameDay(
					messagesBlock[0]?.timestamp,
					messagesBlocks[index - 1]?.[0]?.timestamp,
				);

				return messagesBlock.map((message, i, array) => {
					const isFirstMessage = i === 0;
					const isLastMessage = array.length - 1 === i;

					const props: IMessageContextState = {
						message,
						isFirstMessage,
						isLastMessage,
						isFirstBlock,
						isLastBlock,
						isDifferentDayBlock,
					};

					return props;
				});
			})
			.flat(),
);

export const getUnreadMessagesCount = createDraftSafeSelector(
	getUnreadMessages,
	(messages) => {
		return messages.length;
	},
);
export const getHasUnreadMessages = createDraftSafeSelector(
	getUnreadMessagesCount,
	(count) => !!count,
);

export const getMessageStatus = createDraftSafeSelector(
	messagesSelectors.selectById,
	(message) => !!message?.timestamp,
);

export const getPostbackDataEvent = createDraftSafeSelector(
	[
		messagesSelectors.selectAll,
		(state: IAppState, command_id: IMessageCommandMeta['id']) => command_id,
	],
	(messages, command_id): IChatEventPostbackMessage | undefined => {
		const message = messages.find(
			(message) =>
				isEventMessagePostback(message) &&
				message._data.parameters.metadata.command_id === command_id,
		);
		if (!message || !isEventMessagePostback(message)) return undefined;
		return message;
	},
);

export const getSurveyDataEvent = createDraftSafeSelector(
	[
		getRenderableMessages,
		(state: IAppState, command_id: IMessageCommandMeta['id']) => command_id,
	],
	(messages, command_id): IChatEventSurveyDataMessage | undefined => {
		const message = messages.find(
			(message) =>
				isEventMessageSurveyData(message) &&
				message._data.parameters.metadata.command_id === command_id,
		);
		if (!message || !isEventMessageSurveyData(message)) return undefined;
		return message;
	},
);

export const getFormIsNearEvent = createDraftSafeSelector(
	[
		getRenderableMessages,
		(state: IAppState, command_id: IClientMessage['client_id']) => command_id,
	],
	(messages, command_id) => {
		return messages.some((command, index) => {
			if (
				command.client_id === command_id &&
				((isCommandMessageForm(command) &&
					!command._data.parameters.data.reusable) ||
					isCommandMessageSurvey(command))
			) {
				const event = messages[index + 1];
				if (
					event &&
					(isEventMessageSurveyData(event) || isEventMessagePostback(event))
				) {
					return (
						command.metadata.id === event._data.parameters.metadata.command_id
					);
				}
			}
			return false;
		});
	},
);

export const getLastCommandShowSurveyMessage = createDraftSafeSelector(
	messagesSelectors.selectAll,
	(messages) => {
		const message = messages
			.toReversed()
			.find((message) => message.sender !== SenderSource.USER);
		if (
			isCommandMessageSurvey(message) &&
			!isCommandMessageSurveyInline(message)
		) {
			return message;
		}
		return undefined;
	},
);

export const getLastCommandFormFullscreenMessage = createDraftSafeSelector(
	messagesSelectors.selectAll,
	(messages) => {
		const message = messages
			.toReversed()
			.find((message) => message.sender !== SenderSource.USER);
		if (isCommandMessageFormFullscreen(message)) {
			return message;
		}
		return undefined;
	},
);

export const getWave = createDraftSafeSelector(
	messagesSelectors.selectTotal,
	(count) => (count > 0 ? 'right' : 'left'),
);

export const getLastConfirmedMessage = createDraftSafeSelector(
	messagesSelectors.selectAll,
	(messages) =>
		messages
			.toReversed()
			.find((message) => message.timestamp && !!message._meta.sent)?.timestamp,
);
